export const MANAGER_APPROVAL = {
  APPROVED: 'approved',
  WITHDRAWAL_APPROVED: 'withdrawal_approved',
} as const;

export type ManagerApproval =
  (typeof MANAGER_APPROVAL)[keyof typeof MANAGER_APPROVAL];

export const HR_APPROVAL = {
  APPROVED: 'approved',
} as const;

export type HrApproval = (typeof HR_APPROVAL)[keyof typeof HR_APPROVAL];

export type AbsenceChangeOriginDto = {
  acti_type: number;
  date_from: string;
  date_to: string;
  acti_start_time: string | null;
  acti_end_time: string | null;
};

export type AbsenceDto = {
  id: number;
  acti_user: number;
  acti_type?: number;
  acti_colour_fg?: string;
  acti_colour_bg?: string;
  acti_date: string;
  acti_start_time: string | null;
  acti_end_time: string | null;
  acti_tentative?: boolean;
  all_day: boolean;
  acab_approved_manager?: ManagerApproval | null;
  acab_approved_hr?: HrApproval | null;
  acab_notes?: string;
  acab_export?: boolean;
  block_position: string;
  workday_count?: number;
  show_workday_count?: boolean;
  withdrawal_pending?: boolean;
  withdrawal_requested?: boolean;
  change_pending?: boolean;
  changed_from?: AbsenceChangeOriginDto | null;
};

export type Absence = {
  id: number;
  userId: number;
  typeId?: number;
  colourFg?: string;
  colourBg?: string;
  useNeutralColour?: boolean;
  date: Date;
  dateStr: string;
  startTime: string | null;
  endTime: string | null;
  absenceTentative?: boolean;
  allDay: boolean;
  approvedManager?: ManagerApproval | null;
  approvedHr?: HrApproval | null;
  notes?: string;
  export?: boolean;
  blockPosition: string;
  workdays?: number;
  showWorkdays?: boolean;
  /** The viewer owes a decision on withdrawing this day. */
  withdrawalPending?: boolean;
  /** Somebody has already asked for this day to be given back. */
  withdrawalRequested?: boolean;
  /**
   * This day is a proposal written over an absence that is still booked.
   *
   * The booking underneath is not in the payload -- one bar a day is drawn and
   * the proposal is the one worth drawing -- so this is the only sign the day
   * holds two rows and is waiting on a decision.
   */
  changePending?: boolean;
  /**
   * The entry this proposal is asking to replace, where it is one.
   *
   * The absence underneath never reaches the payload, so this is all the
   * client has to say what the pending decision is actually about -- the same
   * contrast the approver's task spells out, shown to the absentee too.
   */
  changedFrom?: AbsenceChangeOrigin | null;
};

/** What an absence looked like before a change was proposed over it. */
export type AbsenceChangeOrigin = {
  typeId: number;
  dateFrom: string;
  dateTo: string;
  startTime: string | null;
  endTime: string | null;
};

export interface SelectedAbsence {
  userId: number;
  start: string;
  end: string;
  startTime: string | null;
  endTime: string | null;
  absenceIds: number[];
  primaryAbsenceId?: number;
  typeId?: number | null;
  absenceTentative?: boolean;
  approvedManager?: ManagerApproval | null;
  approvedHr?: HrApproval | null;
  notes?: string;
  snapshotAbsences?: Absence[];
  isMultiEvent?: boolean;
}

export type AbsenceLockDto = {
  target_user: number;
  owner_user: number;
  owner_name: string;
  expires_at: string;
  heartbeat_interval_seconds: number;
  ttl_seconds: number;
};

export type AbsenceLock = {
  userId: number;
  ownerUserId: number;
  ownerName: string;
  expiresAt: string;
  heartbeatIntervalSeconds: number;
  ttlSeconds: number;
};

export const ABSENCE_LOCK_STATUS = {
  IDLE: 'idle',
  ACQUIRING: 'acquiring',
  LOCKED: 'locked',
  CONFLICTED: 'conflicted',
  REFRESHING: 'refreshing',
  EXPIRED: 'expired',
} as const;

export type AbsenceLockStatus =
  (typeof ABSENCE_LOCK_STATUS)[keyof typeof ABSENCE_LOCK_STATUS];

export const ABSENCE_LOCK_ERROR_STATUSES = [
  ABSENCE_LOCK_STATUS.CONFLICTED,
  ABSENCE_LOCK_STATUS.EXPIRED,
] as const;

export function isAbsenceLockErrorStatus(status: AbsenceLockStatus) {
  return ABSENCE_LOCK_ERROR_STATUSES.includes(
    status as (typeof ABSENCE_LOCK_ERROR_STATUSES)[number],
  );
}

export function isAbsenceLockHeldStatus(status: AbsenceLockStatus) {
  return status === ABSENCE_LOCK_STATUS.LOCKED;
}

export type AbsenceLockState = {
  status: AbsenceLockStatus;
  lock: AbsenceLock | null;
  message: string | null;
};

export type AbsenceTypeDto = {
  id: number;
  acty_name: string;
  acty_group: number | null;
  acty_tentativeable: boolean;
  acty_approvable_manager: boolean;
  acty_approvable_hr: boolean;
  acty_backdateable: boolean;
};

export type AbsenceType = {
  id: number;
  name: string;
  groupId: number | null;
  tentativeable: boolean;
  approvableManager: boolean;
  approvableHr: boolean;
  backdateable: boolean;
};

export type ActivityTypeGroupDto = {
  id: number;
  actg_name: string;
  actg_colour_fg: string | null;
  actg_colour_bg: string | null;
};

export type ActivityTypeGroup = {
  id: number;
  name: string;
  colourFg: string | null;
  colourBg: string | null;
};

export type GroupStatDto = {
  group_id: number;
  group_name: string;
  group_canonical_name: string;
  colour_fg: string | null;
  colour_bg: string | null;
  used: number;
  available: number | null;
  warning: boolean;
  error: boolean;
};

export type GroupStat = {
  groupId: number;
  groupName: string;
  groupCanonicalName: string;
  colourFg: string | null;
  colourBg: string | null;
  used: number;
  available: number | null;
  warning: boolean;
  error: boolean;
};
